// quote-delivery.ts — delivery view of the client quote (Sellsy / PDF contract).
//
// The logistics flat fee and delivery notes affect this client output only; the calculation stays
// line by line. Two independent blocks — outbound logistics and site completion — each with its
// own flat fee, never mixed. Pure function, no pricing effect.

import type { ServiceQuoteLine, ServicesBlock } from "../products/services-pricing";
import { LOGISTICS_OPTS } from "../../data";
import { isFilled, num, round2 } from "../core/core";
import { engineMessage, keyParam } from "../core/messages";

/** One delivery line of the client quote. */
export interface QuoteDeliveryLine {
  /** Prefixed option label, plus the line note when present, or the flat-fee title. */
  title: string;
  quantity: number;
  unitPriceExclVat: number;
  totalExclVat: number;
}

/** A client delivery block: detailed lines or a single flat-fee line, plus the block's overall note. */
export interface QuoteDeliveryBlock {
  lines: QuoteDeliveryLine[];
  /** Overall note, shown under the block, or under the flat-fee line when `flatFee` is set. */
  overallNote: string;
  flatFee: boolean;
}

/** The two independent delivery blocks of the client quote. */
export interface QuoteDelivery {
  outbound: QuoteDeliveryBlock;
  endSite: QuoteDeliveryBlock;
}

/**
 * Filled lines of a block (code set and quantity > 0), with computed PV and title.
 * @param arr The block's service lines.
 * @param prefixKey Message key of the block prefix.
 * @returns The delivery lines.
 */
function filledLines(arr: readonly ServiceQuoteLine[] | undefined, prefixKey: string): QuoteDeliveryLine[] {
  const out: QuoteDeliveryLine[] = [];
  for (const line of arr ?? []) {
    if (!line.code) continue;
    const qty = num(line.quantity) || 0;
    if (qty <= 0) continue; // empty or zero-quantity lines never reach the client output
    const opt = LOGISTICS_OPTS.find((o) => o.code === line.code) ?? LOGISTICS_OPTS[0]!;
    const unitPv = isFilled(line.pv) ? num(line.pv) : opt.pv;
    const note = (line.note ?? "").trim();
    out.push({
      title: note
        ? engineMessage("engineDeliveryTitleWithNote", { prefix: keyParam(prefixKey), option: opt.label, note })
        : engineMessage("engineDeliveryTitle", { prefix: keyParam(prefixKey), option: opt.label }),
      quantity: qty,
      unitPriceExclVat: unitPv,
      totalExclVat: round2(unitPv * qty),
    });
  }
  return out;
}

/**
 * A block, either detailed or collapsed into one flat-fee line whose unit price is the sum of PVs.
 * @param arr The block's service lines.
 * @param prefixKey Message key of the block prefix.
 * @param flatFee Whether the block collapses into a single flat-fee line.
 * @param flatFeeKey Message key of the flat-fee line title.
 * @param overallNote The block's overall note.
 * @returns The delivery block.
 */
function buildBlock(
  arr: readonly ServiceQuoteLine[] | undefined,
  prefixKey: string,
  flatFee: boolean,
  flatFeeKey: string,
  overallNote: string,
): QuoteDeliveryBlock {
  const detail = filledLines(arr, prefixKey);
  const note = (overallNote ?? "").trim();
  if (!flatFee) return { lines: detail, overallNote: note, flatFee: false };
  // Flat fee: filled lines collapse into one, line notes are dropped and only the overall note shows.
  const sum = round2(detail.reduce((s, l) => s + l.totalExclVat, 0));
  const lines = detail.length
    ? [{ title: engineMessage(flatFeeKey), quantity: 1, unitPriceExclVat: sum, totalExclVat: sum }]
    : [];
  return { lines, overallNote: note, flatFee: true };
}

/**
 * Delivery view of the client quote: outbound and site-completion blocks, each detailed or flat-fee.
 * @param P The services block of the quote.
 * @returns The outbound and end-of-site delivery blocks.
 */
export function quoteDeliveryLines(P: ServicesBlock): QuoteDelivery {
  return {
    outbound: buildBlock(
      P.logistics,
      "engineLogisticsPrefix",
      !!P.logisticsFlatFee,
      "engineLogisticsFlatFee",
      P.deliveryNote ?? "",
    ),
    endSite: buildBlock(
      P.endSite,
      "engineEndSitePrefix",
      !!P.siteEndFlatFee,
      "engineEndSiteFlatFee",
      P.endSiteNote ?? "",
    ),
  };
}
